import { Outlet, useNavigate, useOutletContext } from "react-router";
import { useRootLoader } from "@/hooks/useRootLoader";
import { useTenantFeatureFlag } from "@/hooks/useTenantFeatureFlag";
import type { RoleSplitDependentCapability } from "@shared-domain/unizo/permissions";
import { checkCan } from "@/utils/role";
import { ROUTE_403 } from "@/utils/const";
import { useEffect } from "react";

/**
 * PRODUCT_DEV-721: sysadmin から admin へ**降ろした**ルートのガード。
 *
 * 機能フラグ `managerRole` が ON のテナントでは admin で通し、OFF のテナントでは
 * 従来どおり sysadmin を要求する。既存テナントの管理者に、頼まれていない権限が
 * 一斉に生えるのを避けるため、テナント単位で切り替える。
 *
 * サーバ側は `createRouter` の `requires`（同じ表の同じ行）で弾いており、
 * **認可の正はそちら**。ここは直リンクで空の画面に着地させないための出し分け。
 */
export function RoleSplitDescendedGuard({
  capability,
}: {
  /** このガードの下の画面でやること。最低限の役割は表（`@shared-domain/unizo/permissions`）が決める */
  capability: RoleSplitDependentCapability;
}) {
  const { auth } = useRootLoader();
  const navigate = useNavigate();
  const context = useOutletContext();
  const roleSplit = useTenantFeatureFlag("managerRole");
  const allowed = checkCan(auth, capability, { roleSplit });

  useEffect(() => {
    if (!allowed) {
      navigate(ROUTE_403, { replace: true });
    }
  }, [allowed, navigate]);

  if (!allowed) {
    return null;
  }

  return <Outlet context={context} />;
}
